import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, ShoppingCart, SlidersHorizontal, Search, X,
  ChevronDown, ChevronUp, Plus, Minus,
  MapPin, Globe, User, ShoppingBag, Tag, Menu, Navigation,
  Star, ChevronRight,
} from "lucide-react";

import logo from "../../../assets/app/logo.png";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../redux/productSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";
import { addToCart, decrementCartItem } from "../../../redux/cartSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../redux/notificationsSlice";
import { Notifications, SupportAgent, ChatBubble, ShoppingCartOutlined } from "@mui/icons-material";

import {
  electronicsSubcategories,
  electronicsProducts,
} from "../../../UNIVERSAL/CATEGORIES/ElectronicsCategories";
import {
  electronicsColorAttributes,
  electronicsSizeAttributes,
} from "../../ATTRIBUTES/ElectronicsAttributes";

import { fetchAllCommercePartners } from "../../../redux/commercePartnersSlice";
import { getContinent } from "../../../GLOBAL/geo";

/* ─────────────────────────────────────────────────────────────
   GPS & NAVIGATION ONLY
   This page is scoped strictly to the GPS & Navigation
   top-level category. No other electronics category id is
   referenced. Mirrors the Kitchen Electronics page exactly:
   single parent id → subcategories → products.
───────────────────────────────────────────────────────────── */
const GPS_ID = 20000016;

const gpsSubs = electronicsSubcategories.filter(
  (s) => s.parentId === GPS_ID
);

const gpsSubIds = new Set(gpsSubs.map((s) => s.id));

const gpsProds = electronicsProducts.filter((p) =>
  gpsSubIds.has(p.parentId as number)
);

const gpsProdIds = new Set(gpsProds.map((p) => p.id));

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS — deep teal / emerald navigation theme
═══════════════════════════════════════════════════════ */
const T = {
  bg:           "#FFFFFF",
  bgSoft:       "#F0FDF9",
  bgMuted:      "#CCFBF1",
  border:       "#99F6E4",
  muted:        "#065F46",
  mutedSoft:    "#34D399",
  teal:         "#0D9488",
  tealLight:    "#5EEAD4",
  tealDim:      "rgba(13,148,136,0.12)",
  emerald:      "#059669",
  mint:         "#6EE7B7",
  red:          "#E50914",
  globalRed:    "#CC0000",
  gold:         "#FEBA02",
  grad:         "linear-gradient(135deg,#022c22 0%,#065F46 40%,#0D9488 68%,#5EEAD4 100%)",
  gradText:     "linear-gradient(90deg,#0D9488,#5EEAD4)",
  navy:         "#011a14",
  navyMid:      "#022c22",
  tealOutline:  "1.5px solid rgba(13,148,136,0.40)",
};

const GRADIENT = T.grad;
const NAVY     = T.navy;

/* EUR formatter */
const fmtEur = (rawRate: number) =>
  `€${rawRate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ═══════════════════════════════════════════════════════
   COSMIC CSS — deep teal / navigation palette
═══════════════════════════════════════════════════════ */
const COSMIC_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { display: none; }
  input, button { font-family: inherit; }

  @keyframes gpSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes gpAurora {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes gpShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes gpOrb1 {
    0%   { transform: translate(0,0) scale(1); }
    33%  { transform: translate(58px,-34px) scale(1.10); }
    66%  { transform: translate(-26px,44px) scale(0.94); }
    100% { transform: translate(0,0) scale(1); }
  }
  @keyframes gpOrb2 {
    0%   { transform: translate(0,0); }
    33%  { transform: translate(-62px,22px) scale(1.08); }
    66%  { transform: translate(32px,-52px) scale(1.04); }
    100% { transform: translate(0,0); }
  }
  @keyframes gpOrb3 {
    0%,100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(20px,28px) scale(1.05); }
  }
  @keyframes starTwinkle {
    0%,100% { opacity:0.10; transform:scale(0.8); }
    50%     { opacity:0.90; transform:scale(1.5); }
  }
  @keyframes gp-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  @keyframes mc-ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}

  .gp-cosmic-bg {
    background: linear-gradient(135deg,
      #010e09 0%, #01160e 14%, #010e09 28%,
      #000a06 42%, #011208 58%, #021a0f 74%,
      #010f09 88%, #010e09 100%
    );
    background-size: 400% 400%;
    animation: gpAurora 20s ease infinite;
  }

  .gp-shimmer-title {
    background: linear-gradient(90deg,
      #0D9488 0%, #5EEAD4 18%, #fff 36%,
      #34D399 52%, #0D9488 68%, #5EEAD4 84%, #fff 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gpShimmer 4s linear infinite;
  }

  .gp-grad-line {
    background: linear-gradient(90deg, #022c22, #065F46, #0D9488, #5EEAD4, #0D9488, #065F46, #022c22);
    background-size: 300% 100%;
    animation: gpSpin 4s linear infinite;
  }

  .gp-card-hover {
    transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
  }
  .gp-card-hover:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 56px rgba(13,148,136,0.26), 0 0 0 2px rgba(13,148,136,0.48) !important;
  }

  .gp-sort-item:hover { background: #CCFBF1; }
  input::placeholder { color: rgba(94,234,212,0.38); }

  /* ── subcategory card (mirrors Kitchen's .sub-card, teal themed) ── */
  .gp-sub-card{
    display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;
    padding:16px 10px;border-radius:16px;
    border:1.5px solid rgba(13,148,136,.35);
    background:#FFFFFF;
    cursor:pointer;transition:all .2s;
    font-size:11px;font-weight:700;color:#011a14;
    text-align:center;
  }
  .gp-sub-card:hover{
    background:rgba(13,148,136,.06);
    border-color:rgba(13,148,136,.55);
    box-shadow:0 4px 16px rgba(13,148,136,.14);
  }
  .gp-sub-card.active{
    background:rgba(13,148,136,.10);
    border-color:#0D9488;
    color:#0D9488;
  }

  .gp-h-btn{
    display:flex;align-items:center;gap:6px;
    background:rgba(94,234,212,.08);
    border:1px solid rgba(94,234,212,.28);
    cursor:pointer;color:#011a14;
    font-size:12px;font-weight:700;
    padding:7px 13px;border-radius:999px;
    flex-shrink:0;white-space:nowrap;
    transition:background .2s;
    font-family:inherit;
  }
  .gp-h-btn:hover{background:rgba(94,234,212,.16);}

  .gp-tab-item{display:flex;flex-direction:column;align-items:center;gap:3px;font-size:10px;font-weight:700;cursor:pointer;color:#065F46;padding:4px 10px;transition:color .2s;}
  .gp-tab-item.active{color:#0D9488;}
`;

/* ═══════════════════════════════════════════════════════
   STARS — cool teal-tinted
═══════════════════════════════════════════════════════ */
const STARS = Array.from({ length: 36 }, (_, i) => ({
  id: i, size: Math.random() * 2.4 + 0.5,
  x: Math.random() * 100, y: Math.random() * 100,
  delay: Math.random() * 8, dur: 2.4 + Math.random() * 5,
  cool: Math.random() > 0.45,
}));

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const SORT_OPTS: { key: SortKey; label: string }[] = [
  { key: "default",    label: "Best Match"       },
  { key: "newest",     label: "New Arrivals"      },
  { key: "price_asc",  label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
  { key: "name_asc",   label: "Name: A → Z"       },
];

/* mobile sidebar/desktop layout tokens (mirrors Kitchen's S.sidebar) */
const SIDEBAR_STYLE: React.CSSProperties = {
  background: "#FFFFFF", borderRadius: 20, padding: 20,
  boxShadow: "0 4px 24px rgba(13,148,136,.10)", position: "sticky",
  top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto",
  border: T.tealOutline,
};

/* ═══════════════════════════════════════
   TICKER (mirrors Kitchen's AnnouncementBar / KitchenTicker)
═══════════════════════════════════════ */
const GpsTicker: React.FC<{ denied: boolean }> = ({ denied }) => {
  const msg = denied
    ? "⚠ LOCATION DENIED — SHOWING ALL GPS & NAVIGATION · ENABLE LOCATION FOR LOCAL DEALS · "
    : "📡 GPS & NAVIGATION — SAT NAVS · TRACKERS · DASH MOUNTS & MORE · ";
  return (
    <div style={{ overflow: "hidden", background: denied ? "rgba(13,148,136,.08)" : "rgba(94,234,212,.08)", borderBottom: `1px solid ${denied ? "rgba(13,148,136,.22)" : "rgba(94,234,212,.22)"}`, height: 22, display: "flex", alignItems: "center" }}>
      <span style={{ display: "inline-block", whiteSpace: "nowrap", animation: "mc-ticker 26s linear infinite", fontFamily: "'DM Sans',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: ".18em", color: denied ? T.teal : T.emerald, paddingLeft: 24 }}>{msg.repeat(6)}</span>
    </div>
  );
};

const GPS: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const authUser         = useSelector((s: RootState) => s.auth.user);
  const authLoaded       = useSelector((s: RootState) => s.auth.authLoaded);
  const userId           = authUser?.userId;
  const notifications    = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount      = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const filteredListings = useSelector((s: RootState) => s.products.filteredProducts);
  const cartTotal        = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  const ratingSummaries  = useSelector((s: RootState) => s.productRatings.summaries);
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile,      setIsMobile]      = useState(window.innerWidth <= 768);
  const [catOpen,       setCatOpen]       = useState(false);
  const [desktopCatOpen,setDesktopCatOpen]= useState(true);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeSort,    setActiveSort]    = useState<SortKey>("default");
  const [sortOpen,      setSortOpen]      = useState(false);
  const [navOpen,       setNavOpen]       = useState(false);
  // activeSubcat = selected subcategory id (null = show all)
  const [activeSubcat,    setActiveSubcat]    = useState<number | null>(null);
  // activeProdGroup = selected product id within a subcategory
  const [activeProdGroup, setActiveProdGroup] = useState<number | null>(null);

  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  const [userCountry,     setUserCountry]     = useState<string | null>(null);
  const [userCity,        setUserCity]        = useState<string | null>(null);
  const [locationDenied,  setLocationDenied]  = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showGlobal,      setShowGlobal]      = useState(false);

  const [variantOpen,   setVariantOpen]   = useState(false);
  const [variantItem,   setVariantItem]   = useState<any | null>(null);
  const [variantMode,   setVariantMode]   = useState<"fashion" | "beauty" | null>(null);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize,  setSelectedSize]  = useState<number | null>(null);
  const [variantQty,    setVariantQty]    = useState(1);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners()); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalProductsByCountry(null)); return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false); dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSubcat(null); setActiveProdGroup(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  const resetFilters = () => {
    setActiveSubcat(null); setActiveProdGroup(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
    dispatch(listenToPublishedProducts());
  };

  useEffect(() => { if (searchQuery.trim()) { setActiveSubcat(null); setActiveProdGroup(null); } }, [searchQuery]);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCountry]);

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty || 0;

  const getVariantMode = (item: any): "fashion" | "beauty" | null => {
    if (!item.variants || !Object.keys(item.variants).length) return null;
    if (Object.values(item.variants).some((v: any) => Array.isArray(v.sizeIds) && v.sizeIds.length)) return "fashion";
    if (Object.values(item.variants).some((v: any) => v.imageUrl && (!v.sizeIds || !v.sizeIds.length))) return "beauty";
    return null;
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (getVariantMode(item) !== null) {
      setVariantItem(item); setVariantMode(getVariantMode(item));
      setSelectedColor(null); setSelectedSize(null); setVariantQty(1); setVariantOpen(true);
      return;
    }
    dispatch(addToCart({ listingId: item.listingId, name: item.name!, price: item.rate!, photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId }));
  };

  const canContinue =
    variantMode === null ||
    (variantMode === "fashion" && selectedColor !== null && selectedSize !== null && variantQty > 0) ||
    (variantMode === "beauty"  && selectedColor !== null && variantQty > 0);

  const previewColorId = useMemo(() => {
    if (!variantItem || !variantMode) return null;
    const colors = variantItem.attributes?.[variantMode === "beauty" ? 10001 : 20002];
    if (!Array.isArray(colors) || !colors.length) return null;
    return selectedColor ?? colors[0];
  }, [variantItem, variantMode, selectedColor]);

  useEffect(() => {
    if (variantOpen && variantMode === "beauty" && variantItem && selectedColor === null) {
      const colors = variantItem.attributes?.[10001];
      if (Array.isArray(colors) && colors.length) setSelectedColor(colors[0]);
    }
  }, [variantOpen, variantMode, variantItem, selectedColor]);

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!, price: variantItem.rate!,
      photo: variantItem.variants?.[selectedColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selectedColor!, ...(variantMode === "fashion" && { sizeId: selectedSize! }),
      qty: variantQty, hostId: variantItem.hostId, brandId: variantItem.brandId,
    }));
    setVariantOpen(false); setVariantItem(null); setSelectedColor(null); setSelectedSize(null); setVariantQty(1);
    if (toCheckout) navigate(`/metacheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0) + variantQty, items: cartItems },
    });
  };

  const goToCheckout = () => {
    if (!cartItems.length) { alert("Your cart is empty"); return; }
    navigate(`/productcheckout/${userId}`, {
      state: { userId, totalQty: cartItems.reduce((s: number, i: any) => s + i.qty, 0), totalPrice: cartItems.reduce((s: number, i: any) => s + i.qty * i.price, 0), items: cartItems },
    });
  };

  const go          = (path: string) => { if (!userId) return; navigate(`/${path}/${userId}`); };
  const goToDetails = (id: string) => navigate(`/productdetails/${id}`);

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  /* ── FILTERED PRODUCTS (GPS & NAVIGATION ONLY) ── */
  const gpsProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;

      // Must belong to the GPS & Navigation category or one of its subcats/products.
      // No other electronics category id is referenced anywhere in this filter.
      const inGps =
        l.categoryId === GPS_ID ||
        gpsSubIds.has(l.subcategoryId) ||
        gpsProdIds.has(l.productId) ||
        gpsProdIds.has(l.productVariantId);

      if (!inGps) return false;

      // Subcategory filter
      if (activeSubcat) {
        const prodsInSub = new Set(
          gpsProds.filter(p => p.parentId === activeSubcat).map(p => p.id)
        );
        const matchesSub =
          l.subcategoryId === activeSubcat ||
          prodsInSub.has(l.productId) ||
          prodsInSub.has(l.productVariantId);
        if (!matchesSub) return false;
      }

      // Product group filter (within a subcat)
      if (activeProdGroup) {
        if (l.productId !== activeProdGroup && l.productVariantId !== activeProdGroup) return false;
      }

      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    if (activeSort === "default" && !showGlobal) {
      const tierOf = (l: any) => brandTier.get(l.brandId) ?? 3;
      list = [...list].sort((a, b) => tierOf(a) - tierOf(b));
    }
    return list;
  }, [filteredListings, activeSubcat, activeProdGroup, searchQuery, activeSort, showGlobal, brandTier]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    gpsProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    return order.map(bId => ({ brandId: bId, items: map.get(bId)! }));
  }, [gpsProducts]);

  /* ── HEADER ── */
  const Header = () => (
    <div style={{ position: "sticky", top: 0, zIndex: 200 }}>
      <div className="gp-cosmic-bg" style={{ position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {STARS.slice(0, 16).map(s => (
            <div key={s.id} style={{ position: "absolute", left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size, borderRadius: "50%", background: s.cool ? "rgba(94,234,212,0.85)" : "rgba(255,255,255,0.62)", animation: `starTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite` }} />
          ))}
        </div>
        <div style={{ position: "absolute", top: "-50%", left: "6%", width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle,rgba(13,148,136,0.44) 0%,transparent 70%)", filter: "blur(36px)", animation: "gpOrb1 14s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "-38%", right: "10%", width: 170, height: 170, borderRadius: "50%", background: "radial-gradient(circle,rgba(94,234,212,0.30) 0%,transparent 70%)", filter: "blur(28px)", animation: "gpOrb2 17s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "-24%", left: "44%", width: 130, height: 130, borderRadius: "50%", background: "radial-gradient(circle,rgba(52,211,153,0.20) 0%,transparent 70%)", filter: "blur(22px)", animation: "gpOrb3 11s ease-in-out infinite", pointerEvents: "none" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 6px", position: "relative", zIndex: 2 }}>
          <div onClick={() => navigate(`/producthome/${userId}`)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
            <div style={{ padding: 2, borderRadius: 10, background: T.grad, display: "flex" }}>
              <img src={logo} alt="Metarelm" style={{ width: 26, height: 26, borderRadius: 8, background: "#fff", objectFit: "contain", padding: 3 }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
            </div>
          </div>
          <span className="gp-shimmer-title" style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? 16 : 20, fontWeight: 700, letterSpacing: -0.3, flexShrink: 0 }}>GPS & Navigation</span>
          {!locationLoading && !locationDenied && userCountry && (
            <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "rgba(255,255,255,.06)" : "rgba(94,234,212,.15)", border: `1px solid ${showGlobal ? "rgba(255,255,255,.18)" : "rgba(94,234,212,.45)"}`, borderRadius: 8, padding: "5px 10px", color: showGlobal ? "rgba(94,234,212,.55)" : T.tealLight, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
              {showGlobal ? <Globe size={11} /> : <MapPin size={11} />}
              <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry}</span>
            </button>
          )}
          {locationLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.06)", borderRadius: 8, padding: "5px 10px", flexShrink: 0 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.tealLight, animation: "gp-pulse 1.2s infinite" }} />
              <span style={{ fontSize: 10, color: "rgba(94,234,212,.45)" }}>Locating…</span>
            </div>
          )}
          <div style={{ flex: 1 }} />
          {!isMobile && (
            <>
              <button onClick={() => navigate(`/metaonboarding/${userId}`)} className="gp-h-btn" style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", color: "#fff" }}><Tag size={13} color={T.tealLight} /> Sell</button>
              <button onClick={() => go("CustomerSupport")} className="gp-h-btn" style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", color: "#fff" }}><SupportAgent style={{ fontSize: 14 }} /> Support</button>
              <button onClick={() => navigate("/MessagesPreview")} className="gp-h-btn" style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", color: "#fff" }}><ChatBubble style={{ fontSize: 14 }} /> Chat</button>
            </>
          )}
          <button onClick={() => isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v)} style={{ background: "rgba(94,234,212,.15)", border: "1px solid rgba(94,234,212,.45)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            <Menu size={14} /><span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
          </button>
          <button onClick={() => navigate(`/MetaCommerce/${userId}`)} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}><Home size={16} /></button>
          <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => navigate(`/notifications/${userId}`)}>
            <Notifications style={{ fontSize: 22, color: T.tealLight }} />
            {unreadCount > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.globalRed, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadCount}</span>}
          </div>
          <button onClick={goToCheckout} style={{ position: "relative", background: cartTotal > 0 ? T.teal : "rgba(255,255,255,.07)", border: `1px solid ${cartTotal > 0 ? "transparent" : "rgba(94,234,212,.25)"}`, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            <ShoppingCart size={16} />
            {!isMobile && cartTotal > 0 && <span>{cartTotal}</span>}
            {cartTotal > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal > 99 ? "99+" : cartTotal}</span>}
          </button>
          {!isMobile && (
            <>
              <button onClick={() => navigate(`/MetaClientDashboard/${userId}`)} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#fff", fontSize: 12, fontWeight: 700 }}><ShoppingBag size={16} /><span>Orders</span></button>
              <button onClick={() => go("profile")} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff" }}><User size={16} /></button>
            </>
          )}
          <button onClick={() => setNavOpen(v => !v)} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", color: "#fff", flexShrink: 0 }}>{navOpen ? <X size={16} /> : <Menu size={16} />}</button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 16px 12px", position: "relative", zIndex: 2 }}>
          <div style={{ display: "flex", alignItems: "center", flex: 1, background: "rgba(94,234,212,.09)", borderRadius: 10, overflow: "hidden", border: `1.5px solid ${searchFocused ? "rgba(94,234,212,.68)" : "rgba(94,234,212,.22)"}`, backdropFilter: "blur(8px)", boxShadow: searchFocused ? "0 0 0 3px rgba(13,148,136,.20)" : "none", transition: "border-color .2s,box-shadow .2s" }}>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} placeholder="Search GPS units, sat navs, trackers, navigation…" style={{ flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent" }} />
            {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "rgba(94,234,212,.5)" }}><X size={13} /></button>}
            <div style={{ width: 42, height: 42, background: T.grad, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Search size={15} color="#fff" /></div>
          </div>
          <button ref={sortBtnRef} onClick={toggleSort} style={{ display: "flex", alignItems: "center", gap: 5, background: sortOpen ? T.teal : "rgba(255,255,255,.07)", border: `1px solid ${sortOpen ? T.teal : "rgba(94,234,212,.25)"}`, borderRadius: 8, padding: "10px 12px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
            <SlidersHorizontal size={13} />
            {!isMobile && <span>{activeSort !== "default" ? SORT_OPTS.find(o => o.key === activeSort)?.label?.split(":")[0] : "Sort"}</span>}
            {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          {(activeSubcat || activeProdGroup || activeSort !== "default") && (
            <button onClick={resetFilters} style={{ display: "flex", alignItems: "center", background: "rgba(255,255,255,.07)", border: "1px solid rgba(94,234,212,.25)", borderRadius: 8, padding: "10px 11px", color: T.tealLight, cursor: "pointer", flexShrink: 0 }}><X size={13} /></button>
          )}
        </div>
        <div className="gp-grad-line" style={{ height: 2, position: "relative", zIndex: 2 }} />
      </div>

      {sortOpen && sortAnchor && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ position: "fixed", top: sortAnchor.top, left: sortAnchor.left, background: "#fff", borderRadius: 14, boxShadow: "0 20px 56px rgba(0,0,0,.22)", zIndex: 9999, minWidth: 200, overflow: "hidden", border: T.tealOutline }}>
            {SORT_OPTS.map(opt => (
              <div key={opt.key} className="gp-sort-item" onClick={() => { setActiveSort(opt.key); setSortOpen(false); setSortAnchor(null); }} style={{ padding: "11px 18px", fontSize: 13, fontWeight: activeSort === opt.key ? 700 : 500, color: activeSort === opt.key ? T.teal : NAVY, background: activeSort === opt.key ? T.bgMuted : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #99F6E4" }}>
                {opt.label}{activeSort === opt.key && <span style={{ color: T.teal }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}

      <AnimatePresence>
        {navOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 599 }} onClick={() => setNavOpen(false)} />
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} style={{ position: "fixed", top: 70, right: 16, width: 220, zIndex: 600, background: "#fff", borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,.20)", border: T.tealOutline, overflow: "hidden" }}>
              {[
                { label: "Profile", icon: <User size={14} />, path: `/profile/${userId}` },
                { label: "Orders",  icon: <ShoppingBag size={14} />, path: `/MetaClientDashboard/${userId}` },
                { label: "Sell",    icon: <Tag size={14} />, path: `/metaonboarding/${userId}` },
                { label: "Support", icon: <SupportAgent style={{ fontSize: 14 }} />, path: `/CustomerSupport/${userId}` },
                { label: "Chat",    icon: <ChatBubble style={{ fontSize: 14 }} />, path: "/MessagesPreview" },
              ].map(item => (
                <button key={item.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: NAVY, borderBottom: "1px solid #99F6E4", background: "none", width: "100%", textAlign: "left", border: "none" }} onClick={() => { navigate(item.path); setNavOpen(false); }}>
                  <span style={{ color: T.teal }}>{item.icon}</span>{item.label}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );


  const SubcategoryCards = ({ horizontal = false }: { horizontal?: boolean }) => (
    <div style={horizontal
      ? { display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }
      : { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }
    }>
      <div
        className={`gp-sub-card${!activeSubcat ? " active" : ""}`}
        style={horizontal ? { minWidth: 80, flexShrink: 0 } : {}}
        onClick={() => { setActiveSubcat(null); setActiveProdGroup(null); setCatOpen(false); }}
      >
        <Navigation size={22} color={!activeSubcat ? "#0D9488" : T.muted} />
        <span style={{ color: !activeSubcat ? "#0D9488" : "#011a14" }}>All</span>
      </div>

      {gpsSubs.map((sub) => {
        const isActive = activeSubcat === sub.id;
        const SubIcon = sub.icon;
        return (
          <div
            key={sub.id}
            className={`gp-sub-card${isActive ? " active" : ""}`}
            style={horizontal ? { minWidth: 80, flexShrink: 0 } : {}}
            onClick={() => { setActiveSubcat(isActive ? null : sub.id); setActiveProdGroup(null); setCatOpen(false); }}
          >
            {SubIcon
              ? <SubIcon size={22} color={isActive ? "#0D9488" : T.muted} />
              : <Navigation size={22} color={isActive ? "#0D9488" : T.muted} />
            }
            <span style={{ color: isActive ? "#0D9488" : "#011a14" }}>{sub.name}</span>
          </div>
        );
      })}
    </div>
  );

  /* ═════════════════════════════
     PRODUCT CHIPS (within an active subcategory)
  ═════════════════════════════ */
  const ProductChips = () => {
    if (!activeSubcat) return null;
    const prods = gpsProds.filter(p => p.parentId === activeSubcat);
    if (!prods.length) return null;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 16 }}>
        {prods.map(p => (
          <div key={p.id}
            style={{ padding: "5px 13px", fontSize: 12, fontWeight: 700, borderRadius: 999, cursor: "pointer",
              color: activeProdGroup === p.id ? "#fff" : T.teal,
              background: activeProdGroup === p.id ? T.teal : "rgba(13,148,136,.08)",
              border: `1.5px solid ${activeProdGroup === p.id ? T.teal : "rgba(13,148,136,.25)"}`,
              transition: "all .18s" }}
            onClick={() => setActiveProdGroup(activeProdGroup === p.id ? null : p.id)}>
            {p.name}
          </div>
        ))}
      </div>
    );
  };

  /* ═════════════════════════════
     MOBILE CATEGORY SHEET
  ═════════════════════════════ */
  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(1,26,20,.75)", zIndex: 400 }} />
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, borderTop: T.tealOutline, boxShadow: "0 -20px 60px rgba(94,234,212,.12)", overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: "rgba(13,148,136,.4)", borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid rgba(13,148,136,.15)", padding: "12px 20px 10px" }}>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 800, fontSize: 11, letterSpacing: "0.15em", color: T.teal }}>📡 GPS CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>
              <SubcategoryCards horizontal={false} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═════════════════════════════
     BRAND ROW HEADER (mirrors Kitchen)
  ═════════════════════════════ */
  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Shop";
    const shopLogo = partner?.image || logo;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid rgba(13,148,136,0.50)`, background: "#fff" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { (e.currentTarget as HTMLImageElement).src = logo; }} />
        </div>
        <span style={{ fontWeight: 900, fontSize: 14, color: NAVY, textDecoration: "underline", textDecorationColor: T.tealLight, textDecorationThickness: 2, textUnderlineOffset: 4 }}>
          {shopName}
        </span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
          onClick={() => navigate(`/StoreHome/${brandId}`)}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff",
            boxShadow: "0 3px 10px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.6)",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={T.teal} />
        </motion.button>
      </div>
    );
  };

  /* ── CARD ── */
  const GPSCard = ({ item }: { item: any }) => {
    const qty = getQty(item.listingId);
    const price = fmtEur(item.rate ?? 0);
    const discount = typeof item.discount === "number" && item.discount > 0 ? item.discount : null;
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const [hovered, setHovered] = useState(false);

    if (isMobile) {
      return (
        <motion.div whileTap={{ scale: 0.95 }} style={{ borderRadius: 18, overflow: "hidden", border: T.tealOutline, boxShadow: `0 4px 20px ${T.tealDim}`, background: "#fff", cursor: "pointer" }} onClick={() => goToDetails(item.listingId)}>
          <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: "linear-gradient(135deg,rgba(13,148,136,.18),rgba(13,148,136,.06))" }}>
            {item.photos?.[0] ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Navigation size={34} color={T.teal} /></div>}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F2,transparent 50%)` }} />
            {discount && <div style={{ position: "absolute", top: 7, left: 7, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 6, zIndex: 5 }}>{discount}% OFF</div>}
            {hasRating && (
              <div style={{ position: "absolute", top: 7, right: 50, zIndex: 6, background: "rgba(1,26,20,.85)", backdropFilter: "blur(8px)", border: "1px solid rgba(94,234,212,.35)", borderRadius: 999, padding: "3px 7px", display: "flex", alignItems: "center", gap: 3 }}>
                <Star size={9} fill={T.tealLight} color={T.tealLight} />
                <span style={{ color: "#fff", fontWeight: 800, fontSize: 9 }}>{summary!.avgRating.toFixed(1)}</span>
              </div>
            )}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "8px 10px 10px", zIndex: 3, pointerEvents: "none" }}>
              <div style={{ fontWeight: 800, fontSize: 10, color: "#fff", lineHeight: 1.25, marginBottom: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>{item.name}</div>
              <span style={{ color: T.red, fontWeight: 900, fontSize: 16 }}>{price}</span>
            </div>
            <div style={{ position: "absolute", top: 7, right: 7, zIndex: 10 }} onClick={e => e.stopPropagation()}>
              {qty > 0 ? (
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ display: "flex", alignItems: "center", gap: 0, background: "rgba(1,26,20,.92)", borderRadius: 20, padding: "4px 6px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(94,234,212,.30)" }}>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}><Minus size={11} color="#fff" /></button>
                  <span style={{ color: "#fff", fontWeight: 900, minWidth: 20, textAlign: "center", fontSize: 12 }}>{qty}</span>
                  <button style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }}><Plus size={11} color="#fff" /></button>
                </motion.div>
              ) : (
                <motion.button whileTap={{ scale: 0.88 }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }} style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ShoppingCartOutlined style={{ fontSize: 18, color: "#fff" }} /></motion.button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div className="gp-card-hover" style={{ borderRadius: 20, overflow: "hidden", border: T.tealOutline, boxShadow: `0 6px 28px ${T.tealDim}`, background: "#fff", cursor: "pointer" }} onClick={() => goToDetails(item.listingId)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <div style={{ width: "100%", aspectRatio: "1/1", position: "relative", overflow: "hidden", background: "linear-gradient(135deg,rgba(13,148,136,.14),rgba(13,148,136,.04))" }}>
          {item.photos?.[0] ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .38s cubic-bezier(.34,1.56,.64,1)", transform: hovered ? "scale(1.08)" : "scale(1)" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Navigation size={52} color={T.teal} style={{ opacity: 0.3 }} /></div>}
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${NAVY}F5 0%,rgba(1,26,20,0.70) 38%,transparent 65%)`, pointerEvents: "none" }} />
          {discount && <div style={{ position: "absolute", top: 8, left: 8, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 7, zIndex: 5 }}>{discount}% OFF</div>}

          <div style={{ position: "absolute", top: discount ? 44 : 10, left: 10, display: "flex", alignItems: "center", gap: 4, background: "rgba(1,26,20,.82)", borderRadius: 6, padding: "3px 7px", zIndex: 5, border: "1px solid rgba(94,234,212,.18)" }}>
            <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.tealLight, animation: "gp-pulse 1.6s infinite", boxShadow: `0 0 5px ${T.tealLight}` }} />
            <span style={{ fontSize: 8, color: T.tealLight, fontWeight: 700, letterSpacing: "0.06em" }}>SIGNAL</span>
          </div>

          {hasRating && (
            <div style={{ position: "absolute", top: 8, right: 56, zIndex: 6, background: "rgba(1,26,20,.85)", backdropFilter: "blur(8px)", border: "1px solid rgba(94,234,212,.35)", borderRadius: 999, padding: "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={10} fill={T.tealLight} color={T.tealLight} />
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 10 }}>{summary!.avgRating.toFixed(1)}</span>
              <span style={{ color: "rgba(94,234,212,.5)", fontSize: 9 }}>({summary!.totalReviews.toLocaleString()})</span>
            </div>
          )}

          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }} onClick={e => e.stopPropagation()}>
            {qty > 0 ? (
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ display: "flex", alignItems: "center", background: "rgba(1,26,20,.92)", borderRadius: 22, padding: "3px 5px", backdropFilter: "blur(8px)", border: "1.5px solid rgba(94,234,212,.30)", gap: 0 }}>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}><Minus size={12} color="#fff" /></button>
                <span style={{ color: "#fff", fontWeight: 900, minWidth: 24, textAlign: "center", fontSize: 14 }}>{qty}</span>
                <button style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }}><Plus size={12} color="#fff" /></button>
              </motion.div>
            ) : (
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={e => { e.stopPropagation(); handleAddToCart(item); }} style={{ width: 48, height: 48, borderRadius: "50%", border: "none", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 3px 14px rgba(229,9,20,.55)" }}><ShoppingCartOutlined style={{ fontSize: 22, color: "#fff" }} /></motion.button>
            )}
          </div>
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 14px 14px", zIndex: 3, pointerEvents: "none" }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#fff", lineHeight: 1.25, marginBottom: 5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", letterSpacing: -0.2 }}>{item.name}</div>
            <span style={{ color: T.red, fontWeight: 900, fontSize: 22, letterSpacing: -0.5, lineHeight: 1 }}>{price}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  /* ═════════════════════════════
     RENDER PRODUCTS (brand-grouped, mirrors Kitchen)
  ═════════════════════════════ */
  const renderProducts = () => {
    const activeSubName = activeSubcat
      ? gpsSubs.find(s => s.id === activeSubcat)?.name
      : null;

    return (
      <div style={{ marginBottom: 28 }}>
        <ProductChips />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 4, height: 22, borderRadius: 2, background: GRADIENT }} />
            <span style={{ fontWeight: 900, fontSize: 15, color: NAVY }}>
              {activeSubName ?? (locationDenied ? "All GPS" : showGlobal ? "Global GPS" : userCity ? `GPS near ${userCity}` : userCountry ?? "GPS & Navigation")}
            </span>
            <span style={{ fontSize: 12, color: T.muted, background: T.bgMuted, padding: "2px 9px", borderRadius: 999 }}>{gpsProducts.length}</span>
          </div>
          {!locationDenied && userCountry && (
            <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? "#CCFBF1" : T.bgSoft, borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? "#99F6E4" : T.tealLight}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.muted : T.teal, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
            </button>
          )}
        </div>

        {gpsProducts.length > 0 ? (
          <>
            {productsByBrand.map(group => (
              <div key={group.brandId}>
                <BrandRowHeader brandId={group.brandId} />
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(210px,1fr))", gap: isMobile ? 12 : 18 }}>
                  {group.items.map((item: any) => <GPSCard key={item.listingId} item={item} />)}
                </div>
              </div>
            ))}
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "72px 24px" }}>
            <div style={{ display: "inline-block", marginBottom: 24 }}>
              <div style={{ width: 88, height: 88, borderRadius: "50%", background: GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(13,148,136,.38)" }}>
                <Navigation size={40} color="#fff" />
              </div>
            </div>
            <h3 style={{ fontWeight: 900, color: NAVY, marginBottom: 8, fontSize: 18, fontFamily: "'Playfair Display',serif" }}>{locationDenied ? "Location needed" : "No GPS devices found"}</h3>
            <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{locationDenied ? "Enable location to see local GPS devices." : "Try adjusting your filters."}</p>
            <button onClick={resetFilters} style={{ padding: "12px 28px", borderRadius: 12, border: "none", background: GRADIENT, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All GPS & Navigation</button>
          </div>
        )}
      </div>
    );
  };

  /* ── VARIANT MODAL ── */
  const VariantModal = () => (
    <AnimatePresence>
      {variantOpen && variantItem && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setVariantOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(8px)" }}>
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }} onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "28px 28px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ width: 44, height: 4, borderRadius: 2, background: "#99F6E4", margin: "0 auto 22px" }} />
            <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
              {variantItem.photos?.[0] && (
                <div style={{ width: 72, height: 72, borderRadius: 14, overflow: "hidden", background: "#F0FDF9", flexShrink: 0 }}>
                  <img src={variantItem.variants?.[previewColorId!]?.imageUrl ?? variantItem.photos[0]} alt={variantItem.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
              )}
              <div>
                <h3 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 15, color: NAVY }}>{variantItem.name}</h3>
                <span style={{ color: T.red, fontWeight: 900, fontSize: 18 }}>{fmtEur(variantItem.rate ?? 0)}</span>
              </div>
            </div>
            {variantMode && (
              <>
                <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>{variantMode === "beauty" ? "Select Shade" : "Select Color"}</h4>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  {Object.keys(variantItem.variants || {}).map(id => {
                    const colorId = Number(id);
                    const color = electronicsColorAttributes.values.find((v: any) => v.id === colorId);
                    return <button key={colorId} onClick={() => { setSelectedColor(colorId); if (variantMode === "fashion") setSelectedSize(null); }} style={{ width: 34, height: 34, borderRadius: "50%", border: selectedColor === colorId ? `3px solid ${NAVY}` : "2px solid #ddd", background: color?.hexCodes?.[0] ?? "#eee", cursor: "pointer", transform: selectedColor === colorId ? "scale(1.15)" : "scale(1)", boxShadow: selectedColor === colorId ? `0 0 0 2px #fff,0 0 0 4px ${T.teal}` : "none", transition: "all .15s" }} />;
                  })}
                </div>
                {variantMode === "fashion" && selectedColor !== null && (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.8 }}>Select Size</h4>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {(variantItem.variants?.[selectedColor]?.sizeIds ?? []).map((sizeId: number) => {
                        const size = electronicsSizeAttributes.values.find((v: any) => v.id === sizeId);
                        return <button key={sizeId} onClick={() => setSelectedSize(sizeId)} style={{ padding: "8px 16px", borderRadius: 10, border: `2px solid ${selectedSize === sizeId ? NAVY : "#99F6E4"}`, fontWeight: 700, fontSize: 13, cursor: "pointer", background: selectedSize === sizeId ? NAVY : "#fff", color: selectedSize === sizeId ? "#fff" : NAVY, transition: "all .15s" }}>{size?.name}</button>;
                      })}
                    </div>
                  </>
                )}
              </>
            )}
            {selectedColor !== null && (variantMode === "beauty" || (variantMode === "fashion" && selectedSize !== null)) && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 20, background: "#F0FDF9", borderRadius: 14, padding: "12px 16px" }}>
                <button onClick={() => setVariantQty(q => Math.max(1, q - 1))} style={{ width: 38, height: 38, borderRadius: 10, border: "2px solid #99F6E4", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" }}><Minus size={14} color={NAVY} /></button>
                <span style={{ fontSize: 22, fontWeight: 900, color: NAVY, minWidth: 30, textAlign: "center" }}>{variantQty}</span>
                <button onClick={() => setVariantQty(q => q + 1)} style={{ width: 38, height: 38, borderRadius: 10, border: `2px solid ${T.teal}`, background: T.teal, display: "grid", placeItems: "center", cursor: "pointer" }}><Plus size={14} color="#fff" /></button>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(false)} style={{ flex: 1, padding: 14, borderRadius: 14, border: `2px solid ${canContinue ? NAVY : "#99F6E4"}`, background: canContinue ? NAVY : "#F0FDF9", color: canContinue ? "#fff" : T.muted, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Add to Cart</button>
              <button disabled={!canContinue} onClick={() => handleVariantContinue(true)} style={{ flex: 1, padding: 14, borderRadius: 14, border: "none", background: canContinue ? GRADIENT : "#F0FDF9", color: canContinue ? "#fff" : T.muted, fontWeight: 800, fontSize: 14, cursor: canContinue ? "pointer" : "not-allowed" }}>Buy Now</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ═════════════════════════════
     MOBILE BOTTOM TABS (mirrors Kitchen)
  ═════════════════════════════ */
  const BottomTabs = () => isMobile && userId ? (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: "#fff", display: "flex", justifyContent: "space-around", alignItems: "center", boxShadow: "0 -4px 24px rgba(13,148,136,.15)", zIndex: 100, borderTop: "1px solid rgba(13,148,136,.18)" }}>
      {[
        { icon: <Home size={22} />,       label: "Home",    active: false, action: () => navigate(`/MetaCommerce/${userId}`) },
        { icon: <Navigation size={22} />, label: "GPS",     active: true,  action: () => {} },
        { icon: <ShoppingCart size={22} />, label: "Cart",  active: false, action: goToCheckout, badge: cartTotal },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/MetaClientDashboard/${userId}`) },
        { icon: <User size={22} />,        label: "Profile",active: false, action: () => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action}
          className={`gp-tab-item${t.active ? " active" : ""}`} style={{ position: "relative" }}>
          <div style={{ position: "relative" }}>
            {React.cloneElement(t.icon as any, { color: t.active ? T.teal : T.muted })}
            {(t as any).badge > 0 && (
              <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{(t as any).badge}</span>
            )}
          </div>
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═════════════════════════════
     RENDER
  ═════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.bgSoft, paddingBottom: isMobile ? 90 : 32, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{COSMIC_CSS}</style>
      <GpsTicker denied={locationDenied} />
      <Header />

      <MobileCategorySheet />

      {/* ── DESKTOP LAYOUT ── */}
      {!isMobile && (
        <div style={{ display: "grid", gap: 24, padding: "24px 28px 0", gridTemplateColumns: desktopCatOpen ? "260px 1fr" : "1fr" }}>
          {desktopCatOpen && (
            <aside style={SIDEBAR_STYLE}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.15em", color: T.teal, marginBottom: 16 }}>📡 GPS & NAVIGATION</div>
              <SubcategoryCards horizontal={false} />
            </aside>
          )}
          <main style={{ minWidth: 0 }}>
            {renderProducts()}
          </main>
        </div>
      )}

      {/* ── MOBILE LAYOUT ── */}
      {isMobile && (
        <div style={{ padding: "14px 12px 0" }}>
          <div style={{ marginBottom: 16 }}>
            <SubcategoryCards horizontal={true} />
          </div>
          {renderProducts()}
        </div>
      )}

      <VariantModal />
      <BottomTabs />
    </div>
  );
};

export default GPS;